import type { ProductosModel } from 'src/app/models/productos/productos.model';
import type { TareasBioModel } from 'src/app/models/tareas/tareas-bio.model';

/**
 * Shared value resolver for legacy and repeatable Tratamientos Control values.
 */
export class TratamientosControlValueResolver {
    public static readPath(source: unknown, path: string): unknown {
        return path.split('.').reduce<unknown>((current, key) => {
            if (current !== null && typeof current === 'object') {
                return (current as Record<string, unknown>)[key];
            }

            return undefined;
        }, source);
    }

    public static resolveOptionId(value: unknown): string {
        if (value === null || value === undefined) {
            return '';
        }

        if (typeof value === 'string' || typeof value === 'number') {
            return String(value).trim();
        }

        if (typeof value === 'object') {
            const record = value as Record<string, unknown>;
            const id = record['id'] ?? TratamientosControlValueResolver.readPath(record, 'value.id');

            return String(id ?? '').trim();
        }

        return '';
    }

    public static parseSerializedIds(value: unknown): Array<string | number> {
        return String(value ?? '')
            .split(/[;,]/)
            .map((item) => item.trim())
            .filter((item) => item !== '')
            .map((item) => {
                const numericValue = Number(item);

                return Number.isFinite(numericValue) ? numericValue : item;
            });
    }

    public static getProductIdentityCandidates(value: unknown): string[] {
        const candidates = [
            TratamientosControlValueResolver.resolveOptionId(value),
            TratamientosControlValueResolver.readPath(value, 'producto.id'),
            TratamientosControlValueResolver.readPath(value, 'producto.id_producto'),
            TratamientosControlValueResolver.readPath(value, 'producto.value.id'),
            TratamientosControlValueResolver.readPath(value, 'value.id'),
            TratamientosControlValueResolver.readPath(value, 'value.producto.id'),
        ];

        return Array.from(new Set(
            candidates
                .map((candidate) => String(candidate ?? '').trim())
                .filter((candidate) => candidate !== '')
        ));
    }

    public static resolveProductReference(product: TareasBioModel | null): string {
        const candidates = [
            TratamientosControlValueResolver.readPath(product, 'producto.referencia'),
            TratamientosControlValueResolver.readPath(product, 'referencia'),
            TratamientosControlValueResolver.readPath(product, 'producto.num_registro'),
            TratamientosControlValueResolver.readPath(product, 'num_registro'),
        ];

        for (const candidate of candidates) {
            const normalized = String(candidate ?? '').trim();

            if (normalized !== '') {
                return normalized;
            }
        }

        return '';
    }

    public static getBioProductValue(product: TareasBioModel['producto']): ProductosModel | null {
        if (typeof product !== 'object' || product === null) {
            return null;
        }

        return product;
    }
}
